import type {
  AssignHardwarePackageToVariantDto,
  CreateHardwareVariantDto,
  HardwareVariant,
  SerializedHardwareItem,
  SerializedHardwareVariant,
  SerializedInventoryMeter,
  UpdateHardwareVariantDto,
} from '@enmon/commons/lib/tools';
import { buildQuery } from '@enmon/commons/lib/tools/utils/buildQuery';
import type { StrapiApiResponse } from 'api/types/strapiApiResponse';
import { BASE_REST_API_URL } from 'common/constants';
import type { BaseAPIParams } from 'lib/utils/BaseAPIParams';
import type { PopulatedHardwareVariant } from '../../routes/portfolio/meters/routes/detail/types';
import { BaseApiClient } from './baseApiClient';

export type HardwareVariantsFilter = BaseAPIParams<SerializedHardwareVariant> & {
  hardwareVariants?: SerializedHardwareVariant['id'][];
  inventoryMeters?: SerializedInventoryMeter['id'][];
};
export type DeleteHardwareVariantArg = HardwareVariant['id'];
export type CreateHardwareVariantArg = CreateHardwareVariantDto;
export type UpdateHardwareVariantArg = {
  id: HardwareVariant['id'];
  values: UpdateHardwareVariantDto;
};
export type AssignHardwarePackageToVariantArg = AssignHardwarePackageToVariantDto;

export type FindHardwareVariantsResult = StrapiApiResponse<SerializedHardwareVariant>;
export type DeleteHardwareVariantResult = void;
export type CreateHardwareVariantResult = SerializedHardwareVariant;
export type UpdateHardwareVariantResult = SerializedHardwareVariant;
export type AssignHardwarePackageToVariantResult = {
  result: SerializedHardwareItem[];
  failed: Error[];
  skippedInventoryMeterIds: SerializedInventoryMeter['id'][];
};

export class HardwareVariantsApiClient extends BaseApiClient {
  constructor(jwt: string) {
    super('hardware-variants', BASE_REST_API_URL, jwt);
  }

  public async findOne(
    id: SerializedHardwareVariant['id'],
    filter: Pick<HardwareVariantsFilter, 'fields' | 'populate'>,
    signal: AbortSignal | undefined,
  ): Promise<PopulatedHardwareVariant> {
    const { fields, populate } = filter;

    const response = await this.axios.get<{ data: PopulatedHardwareVariant }>(`${id}`, {
      params: buildQuery<SerializedHardwareVariant>({
        filters: {},
        fields,
        populate,
      }),
      signal,
    });

    return response.data.data;
  }

  public async find(
    filter: HardwareVariantsFilter,
    signal: AbortSignal | undefined,
  ): Promise<FindHardwareVariantsResult> {
    const { fields, hardwareVariants, inventoryMeters, page, pageSize, populate, sort } = filter;

    const response = await this.axios.get<FindHardwareVariantsResult>('', {
      params: buildQuery<SerializedHardwareVariant>({
        sort,
        pagination: {
          page,
          pageSize,
        },
        filters: {
          id: {
            $in: hardwareVariants,
          },
          inventory_meter: {
            id: {
              $in: inventoryMeters,
            },
          },
        },
        fields,
        populate,
      }),
      signal,
    });

    return response.data;
  }

  public async create(values: CreateHardwareVariantArg): Promise<CreateHardwareVariantResult> {
    const response = await this.axios.post<CreateHardwareVariantResult>('', { data: values });

    return response.data;
  }

  public async update({ id, values }: UpdateHardwareVariantArg): Promise<UpdateHardwareVariantResult> {
    const response = await this.axios.put<UpdateHardwareVariantResult>(`${id}`, { data: values });

    return response.data;
  }

  public async delete(id: DeleteHardwareVariantArg): Promise<DeleteHardwareVariantResult> {
    const response = await this.axios.delete<DeleteHardwareVariantResult>(`${id}`);

    return response.data;
  }

  public async assignHardwarePackageToVariant(
    args: AssignHardwarePackageToVariantDto,
  ): Promise<AssignHardwarePackageToVariantResult> {
    const response = await this.axios.post<AssignHardwarePackageToVariantResult>(`assign-hardware-package`, args);

    return response.data;
  }
}
